// src/components/dashboard/BadgesGrid.tsx
import { useState } from 'react';
import { BadgeModal } from '../common/BadgeModal';
import { Trophy, Lock } from 'lucide-react';
import { useBadges } from '../../contexts/BadgeContext';
import { useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import mediaService from '../../services/mediaService';
import {
  Badge,
  StudentBadgeProgress,
  mergeDefinitionWithStudent,
} from '../../types/badges';

const FALLBACK_COLOR = '#4f46e5';

type BadgesGridProps = {
  // this should be the array from /api/profile.badges
  studentBadges?: StudentBadgeProgress[];
};

export const BadgesGrid = ({ studentBadges = [] }: BadgesGridProps) => {
  const { badges: defs, isLoading } = useBadges();
  const { userProfile } = useContext(UserContext);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);

  if (isLoading) {
    return (
      <div className="text-center p-6 bg-gray-100 rounded-lg">
        <p className="text-gray-600">Loading badges…</p>
      </div>
    );
  }

  // normalize + merge
  const merged: Badge[] = (defs || []).map((def) => {
    // find a student record for THIS badge
    const student = studentBadges.find(
      (sb) => sb.badgeId === def.id || sb.badgeName === def.name
    );
    const badge = mergeDefinitionWithStudent(def, student);

    // pick what image to show on the card
    const levelImg =
      student?.levelImageUrl ||
      (student?.currentLevel ? def.levelImages[student.currentLevel] : undefined);

    return {
      ...badge,
      // if student is at a level, show that level image; else show base badge
      badgeImageUrl: levelImg ?? def.badgeImageUrl,
      color: def.color || FALLBACK_COLOR,
    };
  });

  // split into earned vs available
  const earnedBadges = merged.filter((b) => b.currentXP > 0);
  const availableBadges = merged.filter((b) => b.currentXP === 0);

  const getProgressPercent = (badge: Badge) => {
    // student current xp for THIS badge
    const xp = badge.currentXP ?? 0;
    // total needed for top level (max of thresholds)
    const thresholds = Object.values(badge.levelThresholds || {});
    if (!thresholds.length) return 0;
    const max = Math.max(...thresholds);
    return Math.min(Math.round((xp / max) * 100), 100);
  };

  return (
    <div className="space-y-8">
      {/* Earned */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <Trophy className="h-6 w-6 text-indigo-600" />
          <h3 className="text-xl font-bold text-gray-800">Earned Badges</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {earnedBadges.map((badge) => {
            const pct = getProgressPercent(badge);
            const safeColor = badge.color || FALLBACK_COLOR;

            return (
              <div
                key={badge.id}
                onClick={() => setSelectedBadge(badge)}
                className="relative flex flex-col items-center p-6 rounded-lg border bg-white shadow hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer"
                style={{ borderColor: safeColor }}
              >
                <div
                  className="w-20 h-20 mb-4 rounded-full overflow-hidden flex items-center justify-center"
                  style={{ backgroundColor: `${safeColor}10` }}
                >
                  {badge.badgeImageUrl ? (
                    <img
                      src={badge.badgeImageUrl?.startsWith('https://')
                        ? badge.badgeImageUrl
                        : mediaService.getDirectUrl(userProfile?.orgId || '', String(badge.badgeImageUrl))}
                      alt={badge.name}
                      className="w-full h-full object-contain"
                    />
                  ) : null}
                </div>

                <h3 className="text-lg font-bold text-center text-gray-900">
                  {badge.name}
                </h3>
                <p className="text-sm text-center text-gray-600 mb-3">
                  {badge.description}
                </p>

                <div className="w-full">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>{badge.slug}</span>
                    <span>{badge.currentXP} XP</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: safeColor,
                      }}
                    />
                  </div>
                  <div className="text-xs text-center text-gray-600 mt-1">
                    Level:{' '}
                    {badge.currentLevel && badge.currentLevel !== 'none'
                      ? badge.currentLevel
                      : 'None'}
                  </div>
                </div>
              </div>
            );
          })}

          {earnedBadges.length === 0 && (
            <div className="col-span-full text-center py-6 text-gray-500">
              You haven’t earned any badges yet.
            </div>
          )}
        </div>
      </div>

      {/* Available */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <Lock className="h-6 w-6 text-gray-400" />
          <h3 className="text-xl font-bold text-gray-800">Available Badges</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {availableBadges.map((badge) => (
            <div
              key={badge.id}
              onClick={() => setSelectedBadge(badge)}
              className="flex flex-col items-center p-6 rounded-lg border border-gray-200 bg-gray-50 cursor-pointer hover:shadow transition"
            >
              <div className="w-20 h-20 mb-4 rounded-full bg-gray-200 flex items-center justify-center opacity-60">
                {badge.badgeImageUrl ? (
                  <img
                    src={badge.badgeImageUrl?.startsWith('https://')
                      ? badge.badgeImageUrl
                      : mediaService.getDirectUrl(userProfile?.orgId || '', String(badge.badgeImageUrl))}
                    alt={badge.name}
                    className="w-full h-full object-contain"
                  />
                ) : null}
              </div>
              <h3 className="text-lg font-semibold text-gray-500">
                {badge.name}
              </h3>
              <p className="text-sm text-center text-gray-400 mb-2">
                {badge.description}
              </p>
              <div className="text-xs text-gray-400">0 XP • Locked</div>
            </div>
          ))}
        </div>
      </div>

      {/* modal */}
      <BadgeModal
        badge={selectedBadge!}
        isOpen={selectedBadge !== null}
        onClose={() => setSelectedBadge(null)}
      />
    </div>
  );
};
